import React, { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import { cn } from '../../../components/TwoDays/model/derive';
import { LOCKED_STATUS, STEPS, setupMode } from '../../../components/TwoDays/ops/opsModel';
import {
  BriefStep,
  CheckpointsStep,
  ProjectStep,
  StartEndStep,
  TasksStep,
  TeamsStep,
} from '../../../components/TwoDays/ops/SetupSteps';
import LiveTasksStep from '../../../components/TwoDays/ops/LiveTasksStep';

const STEP_VIEW = {
  project: ProjectStep,
  brief: BriefStep,
  teams: TeamsStep,
  tasks: TasksStep,
  checkpoints: CheckpointsStep,
  startEnd: StartEndStep,
};

/**
 * Setup (Ops) — 프로젝트 하나를 6단계로 준비한다.
 * Project 를 만들어야 나머지 단계가 열린다. 단계마다 Save 하면 Done, 저장 안 한 변경이 있으면 Not saved.
 */
const OpsSetup = () => {
  const { state, dispatch, project, loading, api, toast: pushToast } = useOutletContext();
  const [stepKey, setStepKey] = useState('project');
  // 다른 프로젝트를 열거나 새로 만들면 아직 안 끝낸 첫 단계부터 (새로 만들면 Brief)
  const [seenId, setSeenId] = useState(project?.id ?? null);
  // 성공 토스트 — Ops 레이아웃의 ToastStack(오른쪽 아래)으로 보낸다
  const setToast = (title) => title && pushToast?.({ type: 'success', state: 'white', title });
  // ?step=tasks — Ops 알림(태스크 거절)에서 바로 그 단계로
  const [searchParams, setSearchParams] = useSearchParams();
  const askedStep = STEPS.some((s) => s.key === searchParams.get('step'))
    ? searchParams.get('step')
    : null;
  if ((project?.id ?? null) !== seenId) {
    setSeenId(project?.id ?? null);
    setStepKey(
      project
        ? (askedStep ?? STEPS.find((s) => !project.done[s.key])?.key ?? 'startEnd')
        : 'project',
    );
  }
  useEffect(() => {
    if (!askedStep || !project) return;
    setStepKey(askedStep);
    setSearchParams({}, { replace: true });
  }, [askedStep, project, setSearchParams]);
  // 만들기에 성공하면 토스트
  const stepApi = {
    ...api,
    toast: setToast,
    create: async (fields) => {
      const ok = await api.create(fields);
      if (ok) setToast('Project created');
      return ok;
    },
  };

  const index = STEPS.findIndex((s) => s.key === stepKey);
  const goNext = () => setStepKey(STEPS[Math.min(index + 1, STEPS.length - 1)].key);
  const mode = setupMode(project);
  // 시작한 뒤에는 단계마다 무엇을 바꿀 수 있는지 보여 준다 (End time editable · Read-only …)
  const statusOf = (key) => {
    if (state.dirty[key]) return 'Not saved';
    if (mode !== 'draft') return LOCKED_STATUS[mode][key];
    if (project?.done[key]) return 'Done';
    return 'Not started';
  };
  const isDone = (key) => mode !== 'draft' || !!project?.done[key];
  /** Project 단계는 저장해야 벗어날 수 있다. 2번부터는 저장하지 않아도 자유롭게 오간다 */
  const projectUnsaved = stepKey === 'project' && (!project || state.dirty.project);

  if (loading) {
    return (
      <div className='flex flex-col gap-6'>
        <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
          Setup
        </h1>
        <div className='h-[420px] animate-pulse rounded-2xl bg-grayscale-gray00' />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
        Setup
      </h1>
      <div className='flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8'>
        <ol className='flex gap-1 overflow-x-auto lg:w-[232px] lg:shrink-0 lg:flex-col'>
          {STEPS.map((s, i) => {
            const status = statusOf(s.key);
            const locked =
              s.key !== stepKey && (projectUnsaved || (!project && s.key !== 'project'));
            const current = s.key === stepKey;
            return (
              <li key={s.key} className='shrink-0'>
                <button
                  type='button'
                  disabled={locked}
                  title={
                    locked
                      ? project
                        ? 'Save the project first'
                        : 'Create the project first'
                      : undefined
                  }
                  aria-current={current ? 'step' : undefined}
                  onClick={() => setStepKey(s.key)}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
                    current ? 'bg-grayscale-gray02/70' : 'hover:bg-grayscale-gray02/40',
                    locked && 'cursor-not-allowed opacity-50 hover:bg-transparent',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-medium',
                      // 완료: 회색으로 채운 원 + 체크 / 지금 단계: 진한 테두리 / 나머지: 연한 테두리
                      isDone(s.key)
                        ? 'border-grayscale-gray04 bg-grayscale-gray02 text-text-secondary'
                        : current
                          ? 'border-text-primary bg-grayscale-gray00 text-text-primary'
                          : 'border-grayscale-gray04 bg-grayscale-gray00 text-text-secondary',
                    )}
                  >
                    {isDone(s.key) ? (
                      <CheckIcon className='h-3 w-3 [&_path]:stroke-[2.2]' />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span className='flex flex-col'>
                    <span
                      className={cn(
                        'whitespace-nowrap text-[15px]',
                        current ? 'font-medium text-text-primary' : 'text-text-primary',
                      )}
                    >
                      {s.label}
                    </span>
                    <span
                      className={cn(
                        'text-[12px]',
                        status === 'Not saved'
                          ? 'text-functional-warning04'
                          : 'text-text-secondary',
                      )}
                    >
                      {status}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        {/* 단계를 오가도 저장 안 한 입력이 남도록 모두 띄워 두고 지금 단계만 보인다 */}
        {STEPS.filter((s) => project || s.key === 'project').map((s) => {
          // 시작 뒤 Tasks 는 진행 중 워크보드 태스크를 다룬다. 나중에 더한 팀(워크보드가 아직 안 열림)은 그 아래에서 계획을 정한다
          const live = s.key === 'tasks' && mode !== 'draft';
          const StepView = live ? LiveTasksStep : STEP_VIEW[s.key];
          const newTeams = live && project.teams.some((t) => !t.boardStarted);
          return (
            <div
              key={`${project?.id ?? 'new'}-${s.key}`}
              className={cn('min-w-0 flex-1', s.key !== stepKey && 'hidden')}
            >
              <StepView project={project} dispatch={dispatch} api={stepApi} onSaved={goNext} />
              {newTeams && (
                <div className='mt-6'>
                  <TasksStep
                    newTeamsOnly
                    project={project}
                    dispatch={dispatch}
                    api={stepApi}
                    onSaved={() => {}}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default OpsSetup;
